import React, { useState, useEffect } from 'react';
import { Upload, message, Card, Image, Button, Spin } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { getBannerList, saveBanner } from '../../api/banner';
import { uploadFile } from '../../api/apartment';
import './index.css';

const BannerManagement = () => {
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // 获取轮播图列表
  const fetchBannerList = async () => {
    try {
      setLoading(true);
      const result = await getBannerList();
      if (result.code === 200 && result.data) {
        // 将URL数组转换为文件列表格式
        const banners = result.data.map((url, index) => ({
          uid: `banner-${index}`,
          name: `banner-${index}`,
          status: 'done',
          url: getFullUrl(url),
          thumbUrl: getFullUrl(url)
        }));
        setFileList(banners);
      }
    } catch (error) {
      message.error('获取轮播图列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBannerList();
  }, []);

  // 获取完整URL
  const getFullUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `${window.location.origin}${url}`;
  };

  // 从完整URL提取相对路径
  const getRelativeUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      try {
        const urlObj = new URL(url);
        return urlObj.pathname;
      } catch (e) {
        return url;
      }
    }
    return url;
  };

  // 保存轮播图到后端
  const saveBanners = async (banners) => {
    try {
      setSaving(true);
      // 提取URL数组（相对路径）
      const urls = banners.map(item => getRelativeUrl(item.url));
      const result = await saveBanner(urls);
      if (result.code === 200) {
        message.success('保存成功');
      } else {
        message.error(result.message || '保存失败');
      }
    } catch (error) {
      message.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  // 上传前校验
  const beforeUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('只能上传图片文件！');
      return false;
    }
    const isLt50M = file.size / 1024 / 1024 < 50;
    if (!isLt50M) {
      message.error('图片大小不能超过 50MB！');
      return false;
    }
    return true;
  };

  // 自定义上传
  const handleUpload = async ({ file, onSuccess, onError }) => {
    try {
      const result = await uploadFile(file);
      if (result.code === 200 && result.data?.url) {
        const newFile = {
          uid: file.uid,
          name: file.name,
          status: 'done',
          url: getFullUrl(result.data.url),
          thumbUrl: getFullUrl(result.data.url)
        };
        
        // 更新文件列表
        const updatedList = [...fileList, newFile];
        setFileList(updatedList);
        
        // 调用保存接口
        await saveBanners(updatedList);
        
        onSuccess(result.data.url);
        message.success('上传成功');
      } else {
        onError(new Error(result.msg || '上传失败'));
        message.error(result.msg || '上传失败');
      }
    } catch (error) {
      onError(error);
      message.error('上传失败');
    }
  };

  // 删除图片
  const handleRemove = async (file) => {
    const updatedList = fileList.filter(item => item.uid !== file.uid);
    setFileList(updatedList);
    
    // 调用保存接口
    await saveBanners(updatedList);
    message.success('删除成功');
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>上传图片</div>
    </div>
  );

  return (
    <div className="banner-management">
      <Card 
        title="轮播图管理" 
        bordered={false}
        extra={
          <span style={{ fontSize: '14px', color: '#999', fontWeight: 'normal' }}>
            支持上传多张轮播图，单个文件不超过 50MB
          </span>
        }
      >
        <Spin spinning={loading || saving}>
          <Upload
            listType="picture-card"
            fileList={fileList}
            beforeUpload={beforeUpload}
            customRequest={handleUpload}
            onRemove={handleRemove}
            accept="image/*"
          >
            {uploadButton}
          </Upload>
          
          <div className="banner-hint">
            <p>说明：</p>
            <ul>
              <li>点击"上传图片"按钮选择图片文件</li>
              <li>支持JPG、PNG、GIF等常见图片格式</li>
              <li>单个文件大小不超过 50MB</li>
              <li>上传后会自动保存，删除图片也会自动保存</li>
            </ul>
          </div>
        </Spin>
      </Card>
    </div>
  );
};

export default BannerManagement;
